import { computed, readonly, ref } from "vue";
import {
  applyA11yDisplayPreferences,
  clearA11yDisplayPreferencesFromDom,
  cycleColorScheme,
  cycleFontSizeLevel,
  FONT_SIZE_LEVELS,
  getCurrentThemeUi,
  getThemeCycleUi,
  readStoredA11yDisplayPreferences,
  persistA11yDisplayPreferences,
  type A11yDisplayPreferences,
  type A11yFontSizeLevel,
} from "@/utils/a11yDisplayPreferences";
import { isAccessibilityToolsEnabled } from "@/utils/accessibilityTools";

const preferences = ref<A11yDisplayPreferences>(
  readStoredA11yDisplayPreferences(),
);

let systemPrefersDark = false;
let colorSchemeMql: MediaQueryList | null = null;
let initialized = false;
let preferencesChangeListener: (() => void) | null = null;

export function registerA11yPreferencesChangeListener(fn: () => void): void {
  preferencesChangeListener = fn;
}

export function unregisterA11yPreferencesChangeListener(): void {
  preferencesChangeListener = null;
}

function notifyPreferencesChange(): void {
  preferencesChangeListener?.();
}

function syncToDocument(): void {
  if (!isAccessibilityToolsEnabled()) {
    clearA11yDisplayPreferencesFromDom();
    return;
  }
  applyA11yDisplayPreferences(preferences.value, systemPrefersDark);
}

function updateSystemTheme(event?: MediaQueryListEvent): void {
  systemPrefersDark = event?.matches ?? colorSchemeMql?.matches ?? false;
  if (preferences.value.colorScheme === "auto") {
    syncToDocument();
  }
}

function persistAndApply(next: A11yDisplayPreferences): void {
  preferences.value = { ...next };
  persistA11yDisplayPreferences(preferences.value);
  syncToDocument();
  notifyPreferencesChange();
}

export function getA11yDisplayPreferences(): A11yDisplayPreferences {
  return { ...preferences.value };
}

export function setA11yDisplayPreferences(
  partial: Partial<A11yDisplayPreferences>,
): void {
  if (!isAccessibilityToolsEnabled()) {
    clearA11yDisplayPreferencesFromDom();
    return;
  }
  persistAndApply({ ...preferences.value, ...partial });
}

export function initA11yDisplayPreferences(): void {
  if (typeof window === "undefined" || initialized) return;

  if (!isAccessibilityToolsEnabled()) {
    clearA11yDisplayPreferencesFromDom();
    initialized = true;
    return;
  }

  preferences.value = readStoredA11yDisplayPreferences();
  colorSchemeMql = window.matchMedia("(prefers-color-scheme: dark)");
  systemPrefersDark = colorSchemeMql.matches;
  syncToDocument();
  colorSchemeMql.addEventListener("change", updateSystemTheme);
  initialized = true;
}

export function useA11yDisplayPreferences() {
  const themeCycleUi = computed(() =>
    getThemeCycleUi(preferences.value.colorScheme),
  );

  const currentThemeUi = computed(() =>
    getCurrentThemeUi(preferences.value.colorScheme),
  );

  const canIncreaseFontSize = computed(
    () => preferences.value.fontSizeLevel < 2,
  );
  const canDecreaseFontSize = computed(
    () => preferences.value.fontSizeLevel > 0,
  );

  const cycleTheme = () => {
    persistAndApply({
      ...preferences.value,
      colorScheme: cycleColorScheme(preferences.value.colorScheme),
    });
  };

  const toggleHighContrast = () => {
    persistAndApply({
      ...preferences.value,
      highContrast: !preferences.value.highContrast,
    });
  };

  const increaseFontSize = () => {
    const level = preferences.value.fontSizeLevel;
    if (level >= 2) return;
    persistAndApply({
      ...preferences.value,
      fontSizeLevel: FONT_SIZE_LEVELS[level + 1] as A11yFontSizeLevel,
    });
  };

  const decreaseFontSize = () => {
    const level = preferences.value.fontSizeLevel;
    if (level <= 0) return;
    persistAndApply({
      ...preferences.value,
      fontSizeLevel: FONT_SIZE_LEVELS[level - 1] as A11yFontSizeLevel,
    });
  };

  const cycleFontSize = () => {
    persistAndApply({
      ...preferences.value,
      fontSizeLevel: cycleFontSizeLevel(preferences.value.fontSizeLevel),
    });
  };

  return {
    preferences: readonly(preferences),
    themeCycleUi,
    currentThemeUi,
    canIncreaseFontSize,
    canDecreaseFontSize,
    cycleTheme,
    toggleHighContrast,
    increaseFontSize,
    decreaseFontSize,
    cycleFontSize,
  };
}
